<template>
  <div class="ticket-table">
    <div class="ticket-table__filter-bar flex items-center gap-2">
      <div class="flex-1 min-w-0 overflow-hidden">
        <FilterPanel
          :has-active-filters="hasActiveFilters"
          :panel-label="t(`${i18nNamespace}.title`)"
          :show-reset="false"
          @reset="resetFilters"
        >
          <div class="mdl-cell mdl-cell--4-col mdl-cell--12-col-phone">
            <SearchField
              v-model="filters.search"
              :placeholder="t(`${i18nNamespace}.search`)"
              @search="onSearch"
              @reset="onSearchReset"
            />
          </div>
        </FilterPanel>
      </div>

      <ActionButton
        custom-class="cdlButton ticket-table__refresh-outer"
        size="small"
        :title="t('ticketsList.tooltip')"
        @click="handleRefresh"
      >
        <span
          class="material-icons ticket-table__refresh-icon"
          :class="{ 'ticket-table__refresh-icon--spin': isRefreshing }"
          aria-hidden="true"
        >refresh</span>
        {{ t(`${i18nNamespace}.refresh`) }}
      </ActionButton>
    </div>

    <div class="ticket-table__panel card-base">
      <DataTable
        :items="items"
        :loading="viewLoader"
        :total-page="totalPage"
        :current-page="currentPage"
        :page-size="pageSize"
        :empty-colspan="headers.length"
        :empty-message="t('reports.noResult')"
        :view-error="viewError"
        :headers="headers"
        :columns="columns"
        :mobile-component="mobileComponent"
        :mobile-props="(item) => ({ item, url: t(detailUrlKey) })"
        :error-message="t('tickets.error')"
        :aria-label="t(`${i18nNamespace}.title`)"
        row-key="id"
        class="ticket-table__datatable"
        @change-page="onChangePage"
        @prev-page="onPrevPage"
        @next-page="onNextPage"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import moment from 'moment'
import DataTable from '@/components/Primitives/Tables/DataTable.vue'
import FilterPanel from '@/components/Primitives/Filters/FilterPanel.vue'
import SearchField from '@/components/Primitives/Search/SearchField.vue'
import ActionButton from '@/components/Primitives/Buttons/ActionButton.vue'
import TicketStatus from '@/components/Feature/Ticket/TicketStatus.vue'
import TicketNumberCell from '@/components/Feature/Ticket/TicketNumberCell.vue'
import TicketSolutionCell from '@/components/Feature/Ticket/TicketSolutionCell.vue'

/**
 * @component TicketTable
 * @description Feature component per tabella ticket con ricerca full-text, paginazione server-side,
 * navigazione al dettaglio e pulsante refresh. Gestisce internamente stato di caricamento, errore
 * e paginazione. Usato da Incident.vue e ChangeRequest.vue tramite prop `service` iniettata.
 *
 * La tabella include sempre le colonne: Numero/Titolo, Data creazione, Soluzione/Asset, Status, Azioni.
 * Con `showSeverity=true` viene inserita la colonna Severity tra Soluzione e Status.
 *
 * Errori di caricamento: viewError si azzera automaticamente dopo 5 s (pattern Colocation).
 * Ricerca: inviata solo se il testo ha almeno 3 caratteri; resettando la ricerca ricarica la lista.
 * Refresh: animazione di rotazione sull'icona; chiamata ignorata se refresh già in corso.
 *
 * @prop {Object}          service        - Service con metodo `getList({ search_by?, page, page_size })` → `Promise<{ data: { data: Array, count: Number }, error }>`. Obbligatorio.
 * @prop {Object|Function} mobileComponent - Componente Vue per la riga mobile (es. IncidentCard). Obbligatorio.
 * @prop {String}          detailUrlKey   - Chiave i18n dell'URL dettaglio (es. `'nav.reportCrListUrl'`). Usato per costruire la rotta di navigazione. Obbligatorio.
 * @prop {String}          i18nNamespace  - Namespace i18n (es. `'ticketsList.ChangeRequest'`). Usato per: panelLabel, placeholder ricerca, label refresh. Obbligatorio.
 * @prop {Boolean}         showSeverity   - `true` = aggiunge colonna `cf_severity_level_picklist` tra la soluzione e lo status. Default: `false`.
 *
 * @slot — nessuno slot esposto
 */

const props = defineProps({
  /**
   * Service object con metodo getList({ search_by, page, page_size }) → Promise<{ data, error }>.
   */
  service: {
    type: Object,
    required: true,
  },
  /**
   * Componente mobile (es. ChangeRequestCard, IncidentCard).
   */
  mobileComponent: {
    type: [Object, Function],
    required: true,
  },
  /**
   * Chiave i18n dell'URL per i dettagli, es. 'nav.reportCrListUrl'.
   */
  detailUrlKey: {
    type: String,
    required: true,
  },
  /**
   * Namespace i18n (es. 'ticketsList.ChangeRequest').
   * Usato per: panelLabel, search placeholder, refresh label, col.number header.
   */
  i18nNamespace: {
    type: String,
    required: true,
  },
  /**
   * true → aggiunge colonna cf_severity_level_picklist tra cf_sold_solution e lo status.
   */
  showSeverity: {
    type: Boolean,
    default: false,
  },
})

const { t } = useI18n()
const router = useRouter()

/* ── Stato reattivo ─────────────────────────────────────────────────────── */

const items = ref([])
const viewLoader = ref(false)
const viewError = ref(false)
const isRefreshing = ref(false)
const pageSize = 10
const totalCount = ref(0)
const currentPage = ref(1)

const filters = ref({
  search: '',
})

/* ── Computed ───────────────────────────────────────────────────────────── */

const totalPage = computed(() => Math.ceil(totalCount.value / pageSize) || 1)
const hasActiveFilters = computed(() => filters.value.search !== '')

const headers = computed(() => {
  const base = [
    t('ticketsList.default.title'),
    t('ticketsList.default.created_at'),
    t('ticketsList.default.solution'),
  ]
  if (props.showSeverity) base.push(t('ticketsList.default.severity_level'))
  base.push(t('ticketsList.default.status'), '')
  return base
})

const columns = computed(() => {
  const base = [
    {
      component: TicketNumberCell,
      props: (item) => ({ ticketNumber: item.ticketNumber, title: item.cf_short_title }),
    },
    { key: 'createdTime', format: (v) => moment(v).format('DD-MM-YYYY') },
    {
      component: TicketSolutionCell,
      props: (item) => ({
        assetType: t('ticketsList.default.' + item.cf_asset_type),
        solution: item.cf_sold_solution,
      }),
    },
  ]
  if (props.showSeverity) base.push({ key: 'cf_severity_level_picklist' })
  base.push(
    { component: TicketStatus, props: (item) => ({ status: item.statusType }) },
    {
      component: ActionButton,
      align: 'right',
      props: () => ({
        title: t('tickets.details'),
        icon: 'arrow_forward',
        iconPosition: 'right',
        customClass: 'cdlButton',
        size: 'small',
      }),
      on: (item) => ({ click: () => goToDetail(item) }),
    },
  )
  return base
})

/* ── Handlers ───────────────────────────────────────────────────────────── */

function goToDetail(item) {
  router.push(`/${t(props.detailUrlKey)}/${t('nav.detail')}/${item.ticketNumber}`)
}

async function loadList() {
  viewLoader.value = false
  totalCount.value = 0

  const params = {
    page: currentPage.value - 1,
    page_size: pageSize,
  }

  if (filters.value.search.length >= 3) {
    params.search_by = filters.value.search
  }

  const { data, error } = await props.service.getList(params)

  if (error) {
    viewError.value = true
    setTimeout(() => {
      viewError.value = false
    }, 5000)
    viewLoader.value = true
    return
  }

  items.value = data?.data ?? []
  totalCount.value = data?.count ?? 0
  viewLoader.value = true
}

function onChangePage(page) {
  currentPage.value = page
  loadList()
}

function onPrevPage() {
  if (currentPage.value > 1) {
    currentPage.value -= 1
    loadList()
  }
}

function onNextPage() {
  if (currentPage.value < totalPage.value) {
    currentPage.value += 1
    loadList()
  }
}

function onSearch() {
  currentPage.value = 1
  loadList()
}

function onSearchReset() {
  filters.value.search = ''
  currentPage.value = 1
  loadList()
}

function resetFilters() {
  filters.value.search = ''
  currentPage.value = 1
  loadList()
}

async function handleRefresh() {
  if (isRefreshing.value) return
  isRefreshing.value = true
  try {
    await loadList()
  } finally {
    isRefreshing.value = false
  }
}

onMounted(() => {
  window.scrollTo(0, 0)
  loadList()
})
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables.scss";

/* margin-bottom tra filtri e panel */
.ticket-table__filter-bar {
  margin-bottom: $cardRowGap;
}

/* padding interno del panel — background/radius/shadow ereditati da .card-base */
.ticket-table__panel {
  padding: 32px; /* $cardPadding=20px è per card compatte; 32px è il padding panel report */
}

/* Pulsante refresh allineato alla stessa altezza di Azzera filtri */
:deep(.ticket-table__refresh-outer) {
  height: 56px; /* $touchTarget=44px min; 56px allineato all'altezza input MDL */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px; /* $headerGap=16px è per header; 8px è il gap icona-testo del pulsante */
  white-space: nowrap;
  line-height: 1;
}

.ticket-table__refresh-icon {
  font-size: $fontNormal; /* 18px */
}

@keyframes ticket-table-spin {
  to { transform: rotate(360deg); }
}

.ticket-table__refresh-icon--spin {
  display: inline-block;
  animation: ticket-table-spin 0.7s linear infinite;
}

@media (prefers-reduced-motion: reduce) {
  .ticket-table__refresh-icon--spin {
    animation: none;
  }
}
</style>
